import { useContext, useEffect, useState } from 'react';
import { NavigateFunction, useNavigate, useSearchParams } from 'react-router-dom';
import { ButtonData } from '../../../../../components/buttonGroup/ButtonGroup';
import { SimpleButton } from '../../../../../components/buttons/SimpleButton';
import { DetailSection, LabelText } from '../../../../../components/generic';
import CurrencyCell from '../../../../../components/tables/cellComponents/CurrencyCell';
import { DateFilterField } from '../../../../../components/tables/filterForms/DateFilterField';
import { FilterField, FilterForm } from '../../../../../components/tables/filterForms/FilterForm';
import {
  FormSelect,
  FormSelectOptions
} from '../../../../../components/tables/filterForms/FormSelect';
import SFCTable from '../../../../../components/tables/SFCTable';
import { useDataContext } from '../../../../../context';
import { SessionContext } from '../../../../../context/SessionContext';
import { useToast } from '../../../../../context/ToastContext';
import { useListFiltersInUrl } from '../../../../../hooks';
import { useGetThemesQuery } from '../../../../../services/endpoints/content/theme';
import {
  Scopes,
  useGetUsersQuery,
  User
} from '../../../../../services/endpoints/people/user';
import { useGetAllOrganizationsQuery } from '../../../../../services/endpoints/places/organization';
import { useGetVenuesQuery } from '../../../../../services/endpoints/places/venue';
import { useGetCoursesQuery } from '../../../../../services/endpoints/schedule/course';
import { getCurrentUser } from '../../../../../services/helper';
import { Course } from '../../../../../types/course.type';
import { Lesson } from '../../../../../types/lesson.type';
import {
  sessionColumns,
  sessionInvoiceColumns,
  sessionOtherColumns,
  simpleScheduleColumns
} from '../../../../../utils/consts/columnDefs/sessionColumns';
import { dateInTimeZone, UTCToDate } from '../../../../../utils/dateUtils';
import { parseSessionsDeepLinkFilters } from '../../../../../utils/listFilterSearchParams';
import { canSeeFinanceSummary } from '../../../../../utils/rbac';

const currentUser = getCurrentUser();

export const userCanEditCreateDelete_Sessions = (user: User | null) => {
  if (
      user?.isSuper ||
      user?.isSupremeAdmin ||
      user?.isAdmin ||
      user?.isGlobal ||
      user?.isLocationAdmin
  ) {
    return true;
  }

  return false;
};

export const userCanView_Sessions = (user: User | null) => {
  if (
      user?.isSuper ||
      user?.isSupremeAdmin ||
      user?.isAdmin ||
      user?.isGlobal ||
      user?.isLocationAdmin ||
      user?.isInstructor
  ) {
    return true;
  }

  return false;
};

export const userCanGenerateInvoice_Sessions = (user: User | null) => {
  if (
      user?.isSuper ||
      user?.isSupremeAdmin ||
      user?.isAdmin ||
      user?.isLocationAdmin
  ) {
    return true;
  }

  return false;
};


export const courseTypeOptions: FormSelectOptions[] = [
  {
    value: 'after_school',
    label: 'After School'
  },
  {
    value: 'camp',
    label: 'Camp'
  },
  {
    value: 'event',
    label: 'Event'
  },
  {
    value: 'silver_spatulas',
    label: 'Silver Spatulas'
  }
];

export const statusOptions: FormSelectOptions[] = [
  {
    value: 'cancelled',
    label: 'Cancelled'
  },
  {
    value: 'offline',
    label: 'Offline'
  },
  {
    value: 'open',
    label: 'Open'
  }
];

export const paymentTypeOptions: FormSelectOptions[] = [
  {
    value: 'website',
    label: 'Website'
  },
  {
    value: 'onsite',
    label: 'Onsite'
  }
];

export const isOnlineOptions: FormSelectOptions[] = [
  {
    value: 'online',
    label: 'Online'
  },
  {
    value: 'in_person',
    label: 'In-person'
  }
];

export const invoiceTypeOptions: FormSelectOptions[] = [
  {
    value: 'per_student',
    label: 'Per student'
  },
  {
    value: 'flat_rate',
    label: 'Flat rate'
  }
];

export const payTypeOptions: FormSelectOptions[] = [
  {
    value: 'flat_rate',
    label: 'Flat Rate'
  },
  {
    value: 'per_hour',
    label: 'Per hour'
  }
];

export const affirmativeOptions: FormSelectOptions[] = [
  {
    label: 'Yes',
    value: 'true'
  },
  {
    label: 'No',
    value: 'false'
  }
];

export const proratedOptions: FormSelectOptions[] = [
  {
    label: 'Yes',
    value: '1'
  },
  {
    label: 'No',
    value: '0'
  }
];

let numberReciptsOptions: FormSelectOptions[] = [];
for (let i = 0; i < 6; i++) numberReciptsOptions.push({ label: `${i}`, value: i });

export const SessionsView = () => {
  const sessionContext = useContext(SessionContext);
  const [searchParams] = useSearchParams();
  const venueIdFromUrl = searchParams.get('venue_id');
  const scopeFromUrl = searchParams.get('scope');
  const contextDefaultScope = sessionContext?.selectedScope || 'upcoming';
  const defaultScope = scopeFromUrl ?? (venueIdFromUrl ? 'all' : contextDefaultScope);
  const { addToast } = useToast();
  const { locationOptions } = useDataContext();
  const [filters, setLocalFilters] = useListFiltersInUrl(
    {
      scope: defaultScope,
      data: { page: 1, entries: 20 }
    },
    { parseExtra: parseSessionsDeepLinkFilters }
  );

  const [scopes, setScopes] = useState<Scopes>({});
  const { data, error, isLoading, isFetching } = useGetCoursesQuery(filters);
  const [courses, setCourses] = useState<Course[]>([]);
  const [buttons, setButtons] = useState<ButtonData[][]>();
  const navigate: NavigateFunction = useNavigate();

  const { data: venueOptionsData } = useGetVenuesQuery({
    data: { entries: 10000, orderBy: { field: 'title', order: 'asc' } }
  });

  let venueOptions: FormSelectOptions[] = [];

  if (venueOptionsData) {
    venueOptions = venueOptionsData.results
        .filter((v) => v.title)
        .map((v) => ({
          value: v.id,
          label: v.title || `Venue ${v.id}`
        }));
  }

  const { data: instructorsData } = useGetUsersQuery({
    scope: 'colleagues',
    data: { entries: 1000, orderBy: { field: 'first_name', order: 'asc' } }
  });

  let instructorOptions: FormSelectOptions[] = [];
  if (instructorsData) {
    const insts = instructorsData.results.map((v: User) => ({
      value: v.id,
      label: `${v.first_name} ${v.last_name}`
    }));
    instructorOptions = insts.sort((a, b) => a.label.localeCompare(b.label));
  }

  const { data: themeOptionsData } = useGetThemesQuery({ data: { entries: 1000, orderBy: { field: 'name', order: 'asc' } } });

  let themeOptions: FormSelectOptions[] = [];

  if (themeOptionsData) {
    themeOptions = themeOptionsData.results.map((v) => ({
      value: v.id,
      label: v.name || `Theme ${v.id}`
    }));
  }

  const { data: organizationOptionsData } = useGetAllOrganizationsQuery({
    data: { entries: 1000, orderBy: { field: 'name', order: 'asc' } }
  });

  let organizationOptions: FormSelectOptions[] = [];

  if (organizationOptionsData) {
    organizationOptions = organizationOptionsData.results
        .map(({ id, name }) => ({ value: id, label: name }))
        .sort((a, b) => a.label.localeCompare(b.label));
  }

  useEffect(() => {
    if (sessionContext?.setFilters) {
      sessionContext.setFilters(filters);
    }
  }, [filters, sessionContext]);

  useEffect(() => {
    if (filters.scope) {
      sessionContext?.setSelectedScope(filters.scope);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterFormFields: FilterField[] = [
    {
      name: 'title',
      label: 'Session Title',
      type: 'text',
      value: (filters.data?.filterBy?.find((f) => f.field === 'title')?.value as string) ?? ''
      // element: (
      //   <SearchField name="title" label="Session Title" placeholder="Search session" />
      // )
    },
    {
      name: 'id',
      label: 'ID',
      type: 'number',
      value: (filters.data?.filterBy?.find((f) => f.field === 'id')?.value as string) ?? ''
    },
    {
      name: 'venue_id',
      label: 'Venue',
      type: 'number',
      // label: 'Instructor',
      // value: filters.data?.filterBy?.find((f) => f.field === 'instructor_full_name')?.value as string ?? '',
      element: (
          <FormSelect
              name="venue_id"
              label="Venue"
              options={venueOptions as FormSelectOptions[]}
              selected_Value={
                venueOptions.find(
                    (option) =>
                        String(option.value) ===
                        String(filters.data?.filterBy?.find((f) => f.field === 'venue_id')?.value)
                ) as FormSelectOptions
              }
          />
      )
    },
    ...(currentUser?.isInstructor ? [] : [{
      name: 'instructor_id',
      label: 'Instructor',
      type: 'number',
      element: (
          <FormSelect
              name="instructor_id"
              label="Instructor"
              options={instructorOptions as FormSelectOptions[]}
              selected_Value={
                instructorOptions.find(
                    (option) =>
                        option.value ===
                        filters.data?.filterBy?.find((f) => f.field === 'instructor_id')?.value
                ) as FormSelectOptions
              }
          />
      )
    }]),
    ...(currentUser?.isGlobal
      ? [{
          name: 'location_id',
          label: 'Location',
          type: 'number' as const,
          element: (
              <FormSelect
                  name="location_id"
                  label="Location"
                  options={locationOptions as FormSelectOptions[]}
                  selected_Value={
                    locationOptions.find(
                        (option) =>
                            option.value ===
                            filters.data?.filterBy?.find((f) => f.field === 'location_id')?.value
                    ) as FormSelectOptions
                  }
              />
          )
        }]
      : []),
    {
      name: 'organization_id',
      label: 'Organization',
      type: 'number',
      element: (
          <FormSelect
              name="organization_id"
              label="Organization"
              options={organizationOptions as FormSelectOptions[]}
              selected_Value={organizationOptions.find(
                  (option) =>
                      String(option.value) ===
                      String(filters.data?.filterBy?.find((f) => f.field === 'organization_id')?.value)
              )}
              // selected_Value={filters.data?.filterBy?.find((f) => f.field === 'organization_id')?.value as unknown as FormSelectOptions}
          />
      )
    },
    {
      name: 'status',
      element: (
          <FormSelect
              name="status"
              label="Status"
              options={statusOptions}
              selected_Value={statusOptions.find(
                  (option) =>
                      String(option.value) ===
                      String(filters.data?.filterBy?.find((f) => f.field === 'status')?.value)
              )}
              // selected_Value={filters.data?.filterBy?.find((f) => f.field === 'status')?.value as unknown as FormSelectOptions}
          />
      )
    },
    {
      name: 'course_type',
      element: (
          <FormSelect
              name="course_type"
              label="Session Type"
              options={courseTypeOptions}
              selected_Value={
                filters.data?.filterBy?.find((f) => f.field === 'course_type')
                    ?.value as unknown as FormSelectOptions
              }
          />
      )
    },
    {
      name: 'payment_type',
      element: (
          <FormSelect
              name="payment_type"
              label="Payment Type"
              options={paymentTypeOptions}
              selected_Value={
                filters.data?.filterBy?.find((f) => f.field === 'payment_type')
                    ?.value as unknown as FormSelectOptions
              }
          />
      )
    },
    {
      name: 'is_online',
      element: (
          <FormSelect
              name="is_online"
              label="Program Type"
              options={isOnlineOptions}
              selected_Value={isOnlineOptions.find(
                  (option) =>
                      String(option.value) ===
                      String(filters.data?.filterBy?.find((f) => f.field === 'is_online')?.value)
              )}
              // selected_Value={filters.data?.filterBy?.find((f) => f.field === 'is_online')?.value as unknown as FormSelectOptions}
          />
      )
    },
    {
      name: 'invoice_type',
      element: (
          <FormSelect
              name="invoice_type"
              label="Invoice Type"
              options={invoiceTypeOptions}
              selected_Value={invoiceTypeOptions.find(
                  (option) =>
                      String(option.value) ===
                      String(filters.data?.filterBy?.find((f) => f.field === 'invoice_type')?.value)
              )}
              // selected_Value={filters.data?.filterBy?.find((f) => f.field === 'invoice_type')?.value as unknown as FormSelectOptions}
          />
      )
    },
    {
      name: 'pay_type',
      element: (
          <FormSelect
              name="pay_type"
              label="Instructor Pay Type"
              options={payTypeOptions}
              selected_Value={payTypeOptions.find(
                  (option) =>
                      String(option.value) ===
                      String(filters.data?.filterBy?.find((f) => f.field === 'pay_type')?.value)
              )}
              // selected_Value={filters.data?.filterBy?.find((f) => f.field === 'pay_type')?.value as unknown as FormSelectOptions}
          />
      )
    },
    {
      name: 'can_be_prorated',
      type: 'number',
      operator: 'equals',
      element: (
          <FormSelect
              name="can_be_prorated"
              label="Prorated"
              options={proratedOptions}
              selected_Value={proratedOptions.find(
                  (option) =>
                      String(option.value) ===
                      String(filters.data?.filterBy?.find((f) => f.field === 'can_be_prorated')?.value)
              )}
          />
      )
    },
    // {
    //   name: 'venue_id',
    //   label: 'Venue ID',
    //   type: 'number',
    //   value: filters.data?.filterBy?.find((f) => f.field === 'venue_id')?.value as string ?? '',
    // },
    {
      name: 'theme',
      element: (
          <FormSelect
              name="theme_id"
              label="Theme"
              options={themeOptions}
              selected_Value={themeOptions.find(
                  (option) =>
                      String(option.value) ===
                      String(filters.data?.filterBy?.find((f) => f.field === 'theme_id')?.value)
              )}
              // selected_Value={filters.data?.filterBy?.find((f) => f.field === 'theme_id')?.value as unknown as FormSelectOptions}
          />
      )
    },
    // {
    //   name: 'time',
    //   element: (
    //     <TimeField name="formatted_time" label="Time" />
    //   )
    // },
    {
      name: 'starts_from',
      id: 'starts_on',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="starts_from"
              label="Starts On [From - To]"
          />
      ),
      value:
          (filters.data?.filterBy?.find(
              (f) => f.field === 'starts_on' && f.operator === 'greater_equal_than'
          )?.value as string) ?? ''
    },
    {
      name: 'starts_to',
      id: 'starts_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="starts_to" />,
      value:
          (filters.data?.filterBy?.find(
              (f) => f.field === 'starts_on' && f.operator === 'lesser_equal_than'
          )?.value as string) ?? ''
    },
    {
      name: 'ends_from',
      id: 'ends_on',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="ends_from"
              label="Ends On [From - To]"
          />
      ),
      value:
          (filters.data?.filterBy?.find(
              (f) => f.field === 'ends_on' && f.operator === 'greater_equal_than'
          )?.value as string) ?? ''
    },
    {
      name: 'ends_to',
      id: 'ends_on',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="ends_to" />,
      value:
          (filters.data?.filterBy?.find(
              (f) => f.field === 'ends_on' && f.operator === 'lesser_equal_than'
          )?.value as string) ?? ''
    },
    {
      name: 'created_at_from',
      id: 'created_at',
      operator: 'greater_equal_than',
      element: (
          <DateFilterField
              name="created_at_from"
              label="Created At [From - To]"
          />
      ),
      value:
          (filters.data?.filterBy?.find(
              (f) => f.field === 'created_at' && f.operator === 'greater_equal_than'
          )?.value as string) ?? ''
    },
    {
      name: 'created_at_to',
      id: 'created_at',
      operator: 'lesser_equal_than',
      element: <DateFilterField name="created_at_to" />,
      value:
          (filters.data?.filterBy?.find(
              (f) => f.field === 'created_at' && f.operator === 'lesser_equal_than'
          )?.value as string) ?? ''
    },
    {
      name: 'day_of_week',
      label: 'Day of Week',
      value: (filters.data?.filterBy?.find((f) => f.field === 'day_of_week')?.value as string) ?? ''
    },
    {
      name: 'receipt_count',
      element: (
          <FormSelect
              name="receipt_count"
              label="Number of Receipts"
              options={numberReciptsOptions}
              selected_Value={
                filters.data?.filterBy?.find((f) => f.field === 'receipt_count')
                    ?.value as unknown as FormSelectOptions
              }
          />
      )
    }
  ];

  const sidebars =
      filters?.scope === 'needs_invoice' && canSeeFinanceSummary(currentUser)
          ? [
            <DetailSection title="Summary">
              <LabelText direction="vertical" label="Total Amount Receivable:">
                <CurrencyCell value={String(data?.totals?.total_receivable)} />
              </LabelText>
              <LabelText direction="vertical" label="Total Amount Invoiced:">
                <CurrencyCell value={String(data?.totals?.total_invoiced)} />
              </LabelText>
              <LabelText direction="vertical" label="Difference:">
                <CurrencyCell
                    value={String(
                        (data?.totals?.total_receivable ?? 0) - (data?.totals?.total_invoiced ?? 0)
                    )}
                />
              </LabelText>
            </DetailSection>
          ]
          : undefined;

  useEffect(() => {
    if (!error && !isLoading && data && data.results) {
      if (data.scopes) {
        setScopes(
            data.scopes.reduce((acc: Scopes, curr: Scopes) => ({ ...acc, ...curr }), {} as Scopes)
        );
        setButtons(
            data.scopes.map((sMap) =>
                Object.entries(sMap).map(([scope, title]) => {
                  let scope_count = 0;
                  if (data.scope_counts != null) {
                    scope_count = data.scope_counts[scope] ?? 0;
                  }
                  return {
                    title: `${title} (${scope_count})`,
                    key: scope
                  };
                })
            )
        );
      }
    } else if (error) {
      addToast('Error while loading data', 'error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, error, isLoading]);

  useEffect(() => {
      if (data && data.results) {
        setCourses(
            data.results.map((course) => ({
              ...course,
              lessons: [...(course.lessons ?? [])]
                  .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
                  .map((lesson) => ({ ...lesson, course }))
            }))
        );
    }
  }, [data]);

  const setFilterBy = (filterBy: { operator: string; field: string; value: string }[]) => {
    setLocalFilters((prev) => ({
      ...prev,
      data: {
        ...prev!.data,
        filterBy
      }
    }));
  };

  return (
      <div className="py-2">
        <SFCTable
            name={`${scopes[filters?.scope!] ?? ''} Sessions`}
            columns={sessionColumns.concat(
                ['needs_invoice', 'over_invoiced'].includes(filters?.scope ?? '')
                    ? sessionInvoiceColumns
                    : sessionOtherColumns
            )}
            data={courses}
            count={data?.count}
            defaultScope={filters.scope ?? defaultScope}
            scopes={buttons}
            isLoading={isLoading}
            isFetching={isFetching}
            sidebars={sidebars}
            indexDownloadPath="courses"
            searchbarActive={false}
            page_Size={20}
            filters={filters?.data?.filterBy}
            onScopeChange={(scope: string) => {
              sessionContext?.setSelectedScope(scope);
            }}
            // defaultSort={
            //   {
            //     // id: ['needs_invoice', 'over_invoiced', 'with_waitlist'].includes(filters?.scope ?? '') ? "ends_on" : "starts_on",
            //     id: "starts_on",
            //     desc: false
            //   }}
            expandedItem={{
              columns: simpleScheduleColumns,
              rowKey: 'lessons',
              filterFn: (lesson: Lesson) => {
                const result = new Date(lesson.date) >= new Date(new Date().toDateString());
                return result;
              }
            }}
            filterForm={
              <FilterForm
                  fields={filterFormFields}
                  onApplyFilters={setFilterBy}
                  reset={(resetForm) => {
                    resetForm();
                    sessionContext?.clearFilters();
                  }}
              />
            }
            onFiltersChanged={setLocalFilters}
            newItemEl={
                userCanEditCreateDelete_Sessions(currentUser) && (
                    <SimpleButton
                        variant="sfc-blueberry"
                        height={40}
                        truncate={false}
                        onClick={() => navigate('/admin/schedule/sessions/new')}
                    >
                      New session
                    </SimpleButton>
                )
            }
        />
      </div>
  );
};
